import { Inject, Injectable } from '@angular/core';
import { iFrameCustomerProfiles, type IIFrameProfileOptions } from './iframe-customer-profiles';
import { AdapterContext, ConnectionKind } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import {
    IResourceContext,
    RESOURCE_CONTEXT_DATA
} from 'app/multi-contexts/resource-context.typings';
import { ExposureControlService } from './exposure-control/base-exposure-control.service';

@Injectable({
    providedIn: 'root'
})
export class IFrameService {
    public currentCustomerProfile: IIFrameProfileOptions;
    public connectionKindToLinkedServiceTypesMapping: Map<ConnectionKind, string[]> = new Map<
        ConnectionKind,
        string[]
    >();

    constructor(
        @Inject(RESOURCE_CONTEXT_DATA) contextData: IResourceContext,
        private readonly _ecSvc: ExposureControlService
    ) {
        if (GlobalVariables.enableIFrameMode) {
            this.updateAdapterContextByIframeCustomerProfile(
                AdapterContext.defaultContextOf(contextData),
                multiContextRootModuleAsEntry
                    ? contextData.contextType
                    : window.location.href.match(/[\?&]feature\.iFrameCustomer=([a-z]+)/i)?.[1]
            );
        }
    }

    public updateAdapterContextByIframeCustomerProfile(context: AdapterContext, customerType: string): void {
        this.currentCustomerProfile = iFrameCustomerProfiles[customerType] || undefined;
        this._setConnectionKindToLinkedServiceTypesMapping();
        context.adapterConfig.inlineDataset = !!this.currentCustomerProfile?.inlineDataset;
        context.adapterConfig.referenceById = !!this.currentCustomerProfile?.referenceById;
        context.adapterConfig.tolerateInvalidReference = !!this.currentCustomerProfile?.tolerateInvalidReference;
        context.adapterConfig.dismissEmptyUserProperties = !!this.currentCustomerProfile?.dismissEmptyUserProperties;
        context.adapterConfig.preProcessResource = this.currentCustomerProfile?.preProcessResource;
        context.adapterConfig.postProcessResource = this.currentCustomerProfile?.postProcessResource;
    }

    public isMappingToMultipleConnectors(connectionKind: ConnectionKind) {
        if (
            ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN1 === connectionKind ||
            ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN2 === connectionKind
        ) {
            return this._ecSvc.enableADLSCosmosDataset;
        } else {
            return (
                this.connectionKindToLinkedServiceTypesMapping.has(connectionKind) &&
                this.connectionKindToLinkedServiceTypesMapping.get(connectionKind)?.length > 1
            );
        }
    }

    public getMappedLinkedServiceType(originalLinkedServiceType: string): string {
        if (
            this.currentCustomerProfile?.linkedServiceToConnectionTypeMapping &&
            this.currentCustomerProfile?.connectionTypeToLinkedServiceMapping
        ) {
            const connectionKind: ConnectionKind = this.currentCustomerProfile.linkedServiceToConnectionTypeMapping[
                originalLinkedServiceType
            ] as ConnectionKind;
            return (
                this.currentCustomerProfile.connectionTypeToLinkedServiceMapping[connectionKind] ??
                originalLinkedServiceType
            );
        }

        return originalLinkedServiceType;
    }

    private _setConnectionKindToLinkedServiceTypesMapping() {
        this.connectionKindToLinkedServiceTypesMapping.clear();
        if (this.currentCustomerProfile?.linkedServiceToConnectionTypeMapping) {
            // Generate the `connectionKind` to `linkedServiceTypes` mapping through `linkedServiceToConnectionTypeMapping`.
            Object.entries(this.currentCustomerProfile.linkedServiceToConnectionTypeMapping).forEach(
                ([linkedServiceType, connectionKind]: [string, ConnectionKind | undefined]) => {
                    if (this.connectionKindToLinkedServiceTypesMapping.has(connectionKind)) {
                        this.connectionKindToLinkedServiceTypesMapping.get(connectionKind).push(linkedServiceType);
                    } else if (connectionKind != null) {
                        this.connectionKindToLinkedServiceTypesMapping.set(connectionKind, [linkedServiceType]);
                    }
                }
            );
        }
    }
}
